<template>
  <!-- The card every device without a bespoke face draws: its picture (or
       glyph), its name and its first reading — and, in the picture's corner,
       how long it still has to run. That corner is why this is a component of
       its own: the reading has to come from the device's **live** values (a
       washer that started two minutes ago is not the row the page loaded), and
       the card's own "at work" state has to come from the same place. -->
  <DeviceCardShell
    :size="size"
    :name="device.deviceName"
    :title="device.deviceName"
    :active="atWork"
    :breathing="tone.breathing && atWork"
    :accent="tone.accent"
    class="generic-device-card"
    @click="emit('open', device)"
    @contextmenu.prevent="emit('open', device)">
    <template #corner>
      <ApplianceRuntimeRing :runtime="runtime" :compact="size === 'min'" :accent="tone.accent" />
    </template>

    <template #media>
      <!-- Off: the photo is still there, just unlit — the same grayscale the
           binary sensor's clear face draws (owner, 25.10.2026: *"display device
           thumb image in grayed filter when device is off.. see binary sensor
           widget"*). A device that reports no load state at all (a fridge, a NAS
           volume) is never greyed: it cannot be off. -->
      <DeviceThumbnail
        v-if="device.thumbnailUrl || deviceThumbnailS3Key(device)"
        :url="device.thumbnailUrl"
        :s3-key="deviceThumbnailS3Key(device)"
        :alt="device.deviceName"
        class="generic-device-card__img"
        :class="{ 'generic-device-card__img--unlit': unlit }" />
      <span
        v-else
        class="material-symbols-outlined generic-device-card__glyph"
        :class="{ 'generic-device-card__img--unlit': unlit }"
        >{{ device.icon || 'widgets' }}</span
      >
    </template>

    <template v-if="size !== 'min' && reading" #value>
      <span class="generic-device-card__value">{{ reading }}</span>
    </template>

    <template v-if="facts.length" #facts>
      <span v-for="fact in facts" :key="fact.key" class="generic-device-card__fact" :title="fact.title">{{
        applianceFactText(fact)
      }}</span>
    </template>
  </DeviceCardShell>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DeviceCardShell, { type DeviceCardSize } from './DeviceCardShell.vue';
import DeviceThumbnail from '../device-buttons/DeviceThumbnail.vue';
import ApplianceRuntimeRing from './ApplianceRuntimeRing.vue';
import { deviceThumbnailS3Key } from './deviceWidgetTypes';
import { useApplianceRuntime } from '../../composables/useApplianceRuntime';
import { applianceCardTone } from '../../utils/applianceCardTone';
import { applianceFactText, applianceFacts } from '../../utils/applianceFacts';
import { tt } from '../../utils/consoleText';
import type { Device } from '@/types';

const props = defineProps<{
  device: Device;
  size: DeviceCardSize;
}>();

const emit = defineEmits<{ open: [device: Device] }>();

/** The card is a machine at work while its own values say so — a load is live
 *  either because the switch is on or because a cycle still has time on it. */
const { runtime, running, unlit } = useApplianceRuntime(props.device.id, () => props.device.deviceValues);

const atWork = computed(() => running.value);

/** What the machine *is*: a hob is red, laundry blue, computing green — and
 *  only the ones that do work breathe. */
const tone = computed(() => applianceCardTone(props.device));

/** The readings beside the headline: a cavity, a CPU load, a memory figure.
 *  A mid card is narrow, so it carries one of them and a max card two. */
const facts = computed(() =>
  applianceFacts(props.device.deviceValues, {
    excludeKeys: [primaryKey.value].filter((key): key is string => Boolean(key)),
    limit: props.size === 'mid' ? 1 : 2,
  }),
);

/** The row the headline reading came from, so it is never printed twice. */
const primaryKey = computed<string | null>(() => {
  if (runtime.value) return 'remaining_time';
  for (const row of props.device.deviceValues ?? []) {
    const value = row.currentValue;
    if (value === null || value === undefined || value === '') continue;
    return String(row.valueKey ?? '') || null;
  }
  return null;
});

/**
 * The one number a card carries. A cycle in progress owns it: the machine's
 * own on/off row can read "off" while it counts down (a programme that was
 * started by hand and whose power row was never set), and a stale "Off" beside
 * a running clock is the one thing worse than no reading at all.
 */
const reading = computed(() => {
  if (runtime.value) return tt('Running', 'V teku');
  for (const row of props.device.deviceValues ?? []) {
    const value = row.currentValue;
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'boolean') return value ? tt('On', 'Vklopljeno') : tt('Off', 'Izklopljeno');
    const unit = String((row as { unit?: string | null }).unit ?? '');
    return `${value}${unit}`;
  }
  return null;
});
</script>

<style scoped>
.generic-device-card__img {
  /* Iconic, not photographic — same rule as GenericWidget's face: contain, and no
     inset of our own, so the artwork is as large as the media box allows. */
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.generic-device-card__glyph {
  font-size: 2.4rem;
}

/* The unlit picture: colour drained, a little dimmer — the binary sensor's
   `--clear` recipe, so one "off" looks the same on every card face. */
.generic-device-card__img--unlit {
  filter: grayscale(1);
  opacity: 0.72;
  transition:
    filter 0.3s ease,
    opacity 0.3s ease;
}

.generic-device-card__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--card-ink, #64748b);
}

.generic-device-card__fact {
  overflow: hidden;
  padding: 0 0.3rem;
  border-radius: 9999px;
  background: color-mix(in srgb, currentColor 14%, transparent);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
